import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, CheckCircle, Loader2, ArrowLeft } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { apiService } from '@/services/api';
import { getApiErrorMessage } from '@/lib/apiErrors';

const forgotPasswordSchema = z.object({
  email: z.string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
});

type ForgotPasswordForm = z.infer<typeof forgotPasswordSchema>;

export const ForgotPassword: React.FC = () => {
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError: setFormError,
    clearErrors,
  } = useForm<ForgotPasswordForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    shouldFocusError: false,
    shouldUseNativeValidation: false,
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (rawData: Record<string, unknown>) => {
    try {
      setIsSubmitting(true);
      setError('');
      setSuccess('');
      clearErrors();

      // Manually validate with Zod to catch and handle errors properly
      const validationResult = forgotPasswordSchema.safeParse(rawData);
      
      if (!validationResult.success) {
        // Handle validation errors manually without throwing
        validationResult.error.issues.forEach((err) => {
          if (err.path[0] === 'email') {
            setFormError('email', { type: 'manual', message: err.message });
          }
        });
        return; // Stop execution without throwing
      }

      const data = validationResult.data;
      const response = await apiService.forgotPassword(data);
      
      if (response.success) {
        setSuccess(response.message || 'If the email address exists in our system, you will receive password reset instructions.');
      } else {
        setError(getApiErrorMessage(response, 'An error occurred while processing your request. Please try again.'));
      }
    } catch (err) {
      console.error('Forgot password error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onError = (errors: Record<string, unknown>) => {
    // This should not be called now since we handle validation manually
    if (import.meta.env.DEV) {
      console.log('Form submission errors (should not happen):', errors);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <Logo />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Forgot your password?</h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Enter your email address and we'll send you a link to reset your password
          </p>
        </div>

        <Card className="border-gray-200 dark:border-gray-700">
          <CardHeader>
            <CardTitle>Reset password</CardTitle>
            <CardDescription>
              We'll send reset instructions to your email address
            </CardDescription>
          </CardHeader>

          {success ? (
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2 p-3 text-sm text-green-600 bg-green-50 border border-green-200 rounded-md">
                <CheckCircle size={16} />
                {success}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Please check your email (including your spam folder) for password reset instructions.
              </p>
            </CardContent>
          ) : (
            <form onSubmit={handleSubmit(onSubmit, onError)} noValidate>
              <CardContent className="space-y-4">
                {error && (
                  <div className="flex items-center gap-2 p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
                    <AlertCircle size={16} />
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">Email address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    {...register('email')}
                    className={errors.email ? 'border-red-500' : ''}
                  />
                  {errors.email && (
                    <p className="text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>
              </CardContent>

              <CardFooter className="flex flex-col space-y-4">
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending reset instructions...
                    </>
                  ) : (
                    'Send reset instructions'
                  )}
                </Button>
              </CardFooter>
            </form>
          )}

          <CardFooter className="pt-0">
            <div className="text-center text-sm text-gray-600 dark:text-gray-400 w-full">
              <Link
                to="/login"
                className="inline-flex items-center font-medium text-blue-600 hover:text-blue-500"
              >
                <ArrowLeft className="mr-1 h-4 w-4" />
                Back to sign in
              </Link>
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};